// src/app/core/chatPage/chatCopmonents/shared/AgentSettingsViewModel.ts
import { makeObservable, observable, action } from 'mobx';
import { SingletonClass } from '@/app/utils/SingletonClass';

export type ResponseStyle = 'short' | 'balanced' | 'detailed';

const STORAGE_KEY = 'agent-settings';

interface PersistedSettings {
    showTimeline: boolean;
    webSearchEnabled: boolean;
    deepThinkEnabled: boolean;
    responseStyle: ResponseStyle;
}

export class AgentSettingsViewModel extends SingletonClass {
    private static _instance: AgentSettingsViewModel;

    showTimeline = true;
    webSearchEnabled = false;
    deepThinkEnabled = false;
    responseStyle: ResponseStyle = 'balanced';

    static getInstance(): AgentSettingsViewModel {
        if (!AgentSettingsViewModel._instance) {
            AgentSettingsViewModel._instance = new AgentSettingsViewModel();
        }
        return AgentSettingsViewModel._instance;
    }

    constructor() {
        super();
        this.loadFromStorage();

        makeObservable(this, {
            showTimeline: observable,
            webSearchEnabled: observable,
            deepThinkEnabled: observable,
            responseStyle: observable,

            setShowTimeline: action,
            setWebSearch: action,
            setDeepThink: action,
            setResponseStyle: action,
            reset: action,
        });
    }

    private loadFromStorage(): void {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const data = JSON.parse(raw) as Partial<PersistedSettings>;
            if (typeof data.showTimeline === 'boolean') this.showTimeline = data.showTimeline;
            if (typeof data.webSearchEnabled === 'boolean') this.webSearchEnabled = data.webSearchEnabled;
            if (typeof data.deepThinkEnabled === 'boolean') this.deepThinkEnabled = data.deepThinkEnabled;
            if (data.responseStyle === 'short' || data.responseStyle === 'balanced' || data.responseStyle === 'detailed') {
                this.responseStyle = data.responseStyle;
            }
        } catch (e) {
            console.warn('[AgentSettingsViewModel] load failed', e);
        }
    }

    private persist(): void {
        try {
            const data: PersistedSettings = {
                showTimeline: this.showTimeline,
                webSearchEnabled: this.webSearchEnabled,
                deepThinkEnabled: this.deepThinkEnabled,
                responseStyle: this.responseStyle,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('[AgentSettingsViewModel] save failed', e);
        }
    }

    setShowTimeline(value: boolean): void {
        this.showTimeline = value;
        this.persist();
    }

    setWebSearch(value: boolean): void {
        this.webSearchEnabled = value;
        this.persist();
    }

    setDeepThink(value: boolean): void {
        this.deepThinkEnabled = value;
        this.persist();
    }

    setResponseStyle(value: ResponseStyle): void {
        this.responseStyle = value;
        this.persist();
    }

    reset(): void {
        this.showTimeline = true;
        this.webSearchEnabled = false;
        this.deepThinkEnabled = false;
        this.responseStyle = 'balanced';
        this.persist();
    }

    /** Возвращает суффикс для промпта в зависимости от стиля и тумблеров. */
    getPromptSuffix(): string {
        let suffix = '';

        // Стиль ответа
        if (this.responseStyle === 'short') {
            suffix += '\n\nОтвечай кратко и по делу.';
        } else if (this.responseStyle === 'detailed') {
            suffix += '\n\nОтвечай подробно, с примерами.';
        }

        // Веб-поиск
        if (this.webSearchEnabled) {
            suffix += '\n\nИспользуй инструмент веб-поиска (web_search), если для ответа нужны актуальные данные из интернета.';
        }

        // Deep Think
        if (this.deepThinkEnabled) {
            suffix += '\n\nТщательно обдумай ответ. Используй свои внутренние рассуждения (reasoning) перед тем, как выдать финальный результат.';
        }

        return suffix;
    }
}